<template>
  <div>
    <v-autocomplete
      v-model="localValue"
      :items="countryOptions"
      :loading="isLoading"
      item-text="label"
      item-value="id"
      :label="$t('STOCK.PRODUCTS.ORIGIN_COUNTRY')"
      clearable
      :search-input.sync="search"
      :error-messages="errorMessages"
      @change="emitChange"
      @update:search-input="handleSearch"
    >
      <template v-slot:append-outer>
        <v-btn icon color="primary" @click="showCreateDialog = true">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
    </v-autocomplete>

    <v-dialog v-model="showCreateDialog" max-width="460">
      <v-card>
        <v-card-title>{{ $t("STOCK.PRODUCTS.CREATE_ORIGIN_COUNTRY") }}</v-card-title>
        <v-card-text>
          <v-form ref="countryForm" v-model="validCountryForm" @submit.prevent>
            <v-text-field
              ref="newCountryNameInput"
              v-model="newCountry.name"
              :label="$t('STOCK.PRODUCTS.ORIGIN_COUNTRY_NAME') + '*'"
              :rules="[validations.required()]"
              :error-messages="validationErrors.name || []"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">
            {{ $t("GENERAL.LABELS.CLOSE_BUTTON") }}
          </v-btn>
          <v-btn text color="primary" :loading="isCreating" @click="createCountry">
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from "lodash";
import validations from "@/views/partials/form/Validations.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  CREATE_STOCK_ORIGIN_COUNTRY,
  GET_STOCK_ORIGIN_COUNTRY_OPTIONS,
} from "@/store/stock/stockProducts.module";

export default {
  name: "StockOriginCountryPicker",
  props: {
    value: {
      default: null,
    },
    initialCountry: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      validations,
      localValue: this.value,
      countryOptions: [],
      search: "",
      isLoading: false,
      isCreating: false,
      showCreateDialog: false,
      validCountryForm: false,
      validationErrors: {},
      newCountry: {
        name: "",
      },
    };
  },
  watch: {
    value(value) {
      this.localValue = value;
    },
    initialCountry: {
      handler(country) {
        this.mergeCountryOptions(country ? [country] : []);
      },
      deep: true,
      immediate: true,
    },
    showCreateDialog(value) {
      if (value) {
        this.$nextTick(() => {
          setTimeout(() => this.$refs.newCountryNameInput?.focus(), 100);
        });
      }
    },
  },
  mounted() {
    this.loadCountries("");
  },
  methods: {
    handleSearch: debounce(function (search) {
      this.loadCountries(search || "");
    }, 350),
    loadCountries(search) {
      this.isLoading = true;
      return this.$store
        .dispatch(GET_STOCK_ORIGIN_COUNTRY_OPTIONS, { search })
        .then((response) => {
          this.countryOptions = response.data || [];
          this.mergeCountryOptions(this.initialCountry ? [this.initialCountry] : []);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    mergeCountryOptions(countries) {
      const byId = {};
      [...this.countryOptions, ...(countries || [])].forEach((country) => {
        if (country && country.id) {
          byId[country.id] = country;
        }
      });
      this.countryOptions = Object.values(byId);
    },
    emitChange() {
      this.$emit("input", this.localValue);
      this.$emit("change");
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.validationErrors = {};
      this.newCountry = {
        name: "",
      };
    },
    createCountry() {
      this.$refs.countryForm.validate();
      if (!this.validCountryForm) {
        return;
      }

      this.validationErrors = {};
      this.isCreating = true;

      this.$store
        .dispatch(CREATE_STOCK_ORIGIN_COUNTRY, this.newCountry)
        .then((country) => {
          this.mergeCountryOptions([country]);
          this.localValue = country.id;
          this.emitChange();
          this.closeCreateDialog();
        })
        .catch((error) => {
          this.validationErrors = error?.errors || {};
          this.$store.dispatch(SHOW_SNACK, {
            message: error?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isCreating = false;
        });
    },
  },
};
</script>
